import { AutocompleteArrayInput, ReferenceArrayInput, required, useTranslate } from 'react-admin';
import { Box } from '@mui/material';
import { useWatch } from 'react-hook-form';
import PropTypes from 'prop-types';
import { operatorChoices } from '@/constants/Operators.js';
import { PARAMETER_SUBSET_TYPE } from '@/constants/ParameterSubsetTypes.js';
import { contractorFilterToQuery, collectionPointFilterToQuery, deviceFilterToQuery, deviceModelFilterToQuery } from '@/helpers/filters/filterToQuery.js';
import { validateRequiredSubset } from '@/helpers/parameter/parameterSubsets.js';
import { getCollectionPointOptionText, getDeviceModelOptionText, getDeviceOptionText } from '@/helpers/parameter/parameterSubsetDisplay.js';

const REFERENCE_CONFIG = {
    [PARAMETER_SUBSET_TYPE.MODEL]: {
        reference: 'device-model',
        optionText: getDeviceModelOptionText,
        filterToQuery: deviceModelFilterToQuery,
    },
    [PARAMETER_SUBSET_TYPE.CONTRACTOR]: {
        reference: 'contractor',
        optionText: 'name',
        filterToQuery: contractorFilterToQuery,
    },
    [PARAMETER_SUBSET_TYPE.COLLECTION_POINT]: {
        reference: 'collection-point',
        optionText: getCollectionPointOptionText,
        filterToQuery: collectionPointFilterToQuery,
    },
    [PARAMETER_SUBSET_TYPE.DEVICE]: {
        reference: 'device',
        optionText: getDeviceOptionText,
        filterToQuery: deviceFilterToQuery,
    },
};

const SubsetAutocomplete = ({ source, isFilled, className, ...rest }) => (
    <AutocompleteArrayInput
        source={source}
        className={['custom-select-array-input', isFilled ? 'input-filled' : '', className].filter(Boolean).join(' ')}
        InputLabelProps={isFilled ? { shrink: true } : undefined}
        fullWidth
        disablePortal
        {...rest}
    />
);

SubsetAutocomplete.propTypes = {
    source: PropTypes.string,
    isFilled: PropTypes.bool,
    className: PropTypes.string,
};

export const ParameterSubsetTargetInput = ({ source, typeSource }) => {
    const translate = useTranslate();
    const type = useWatch({ name: typeSource });
    const selected = useWatch({ name: source });
    const isFilled = Array.isArray(selected) && selected.length > 0;
    const label = translate('resources.parameter.fields.subset');
    const requiredMessage = translate('common.validation.required');
    const validate = type ? [required(requiredMessage), value => validateRequiredSubset(value, translate)] : undefined;
    const disabled = !type;

    if (type === PARAMETER_SUBSET_TYPE.OPERATOR) {
        return (
            <Box className='autocomplete-parent'>
                <SubsetAutocomplete
                    key={type}
                    source={source}
                    label={label}
                    choices={operatorChoices}
                    validate={validate}
                    disabled={disabled}
                    isFilled={isFilled}
                />
            </Box>
        );
    }

    const config = REFERENCE_CONFIG[type];
    if (!config) {
        return (
            <Box className='autocomplete-parent'>
                <SubsetAutocomplete key='empty' source={source} label={label} choices={[]} validate={validate} disabled isFilled={isFilled} />
            </Box>
        );
    }

    return (
        <Box className='autocomplete-parent'>
            <ReferenceArrayInput key={type} source={source} reference={config.reference} perPage={100} queryOptions={{ enabled: Boolean(type) }}>
                <SubsetAutocomplete
                    label={label}
                    optionText={config.optionText}
                    filterToQuery={config.filterToQuery}
                    validate={validate}
                    disabled={disabled}
                    isFilled={isFilled}
                />
            </ReferenceArrayInput>
        </Box>
    );
};

ParameterSubsetTargetInput.propTypes = {
    source: PropTypes.string.isRequired,
    typeSource: PropTypes.string.isRequired,
};
